
import React, { useEffect, useState, useRef } from 'react'
import logo from '../header/Logo (6).png'
import { CiBellOn } from "react-icons/ci";
import { CiSearch } from "react-icons/ci";

// Helper to get user ID from JWT in localStorage
function getUserIdFromToken() {
    const token = localStorage.getItem("token");
    if (!token) return null;

    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return decoded.userId || decoded.userID || decoded.userid || decoded.id || decoded.user_id;
    } catch {
        return null;
    }
}

interface UserType {
    id?: number | string;
    name?: string;
    profile_img?: string;
    email?: string;
    mobile?: string;
    [key: string]: any;
}

interface NotificationType {
    id: string;
    title: string;
    description?: string;
    created_at: string;
    read?: boolean;
    type: string;
}

const apiurl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : "";
// Notifications endpoints
const ENDPOINTS = [
    {
        key: 'party',
        url: '/auth/addparty',
        fetchMethod: 'get',
        parse: (entry: any) => ({
            id: `party_${entry.id}`,
            title: "Party Added",
            description: entry.name ? `Party "${entry.name}" was added.` : "A new party was added.",
            created_at: entry.created_at || entry.date || new Date().toISOString(),
            read: false,
            type: 'party'
        })
    },
    {
        key: 'stocks',
        url: '/auth/addstocks',
        fetchMethod: 'get',
        parse: (entry: any) => ({
            id: `stock_${entry.id}`,
            title: "Stock Added",
            description: entry.stock_name ? `Stock "${entry.stock_name}" was added.` : "A new stock was added.",
            created_at: entry.created_at || entry.date || new Date().toISOString(),
            read: false,
            type: 'stock'
        })
    },
    {
        key: 'withdrawal',
        url: '/auth/withdrawals',
        fetchMethod: 'get',
        parse: (entry: any) => ({
            id: `withdrawal_${entry.id}`,
            title: "Amount Withdrawn",
            description: entry.amount ? `An amount of ₹${entry.amount} was withdrawn.` : "A withdrawal was made.",
            created_at: entry.created_at || entry.date || new Date().toISOString(),
            read: false,
            type: 'withdrawal'
        })
    }
];

interface Props {
    searchValue?: string;
    onSearchChange?: (val: string) => void;
}

function Header(props: Props) {
    const {
        searchValue = "",
        onSearchChange = () => {},
    } = props

    // Use actual logged-in user data fetched using user id from token
    const [user, setUser] = useState<UserType | null>(null);
    const [notifications, setNotifications] = useState<NotificationType[]>([]);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);
    const notifDropdownRef = useRef<HTMLDivElement>(null);

    // For profile edit
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [editUser, setEditUser] = useState<UserType | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    // Fetch logged-in user data using https://stockaadvikbackend.aadviktech.com/auth/allusersinfo and match id from token
    useEffect(() => {
        const fetchUser = async () => {
            const id = getUserIdFromToken();
            if (!id) {
                setUser(null);
                return;
            }
            try {
                const res = await fetch(`https://stockaadvikbackend.aadviktech.com/auth/allusersinfo`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
                    },
                });
                if (!res.ok) {
                    setUser(null);
                    return;
                }
                const users = await res.json();
                if (Array.isArray(users)) {
                    const matchUser = users.find((u: any) => String(u.id) === String(id));
                    setUser(matchUser || null);
                } else {
                    setUser(null);
                }
            } catch {
                setUser(null);
            }
        };
        fetchUser();
    }, []);

    // Poll for notifications (using fixed base API url)
    useEffect(() => {
        let isMounted = true;
        const LOCAL_STORAGE_KEY = 'seen_notif_ids';

        const getSeenIds = () => {
            try {
                const v = localStorage.getItem(LOCAL_STORAGE_KEY);
                return v ? JSON.parse(v) : [];
            } catch { return []; }
        };

        const fetchAll = async () => {
            let allNotifs: NotificationType[] = [];
            for (const ep of ENDPOINTS) {
                try {
                    const url = apiurl + ep.url;
                    const res = await fetch(
                        url,
                        {
                            method: ep.fetchMethod.toUpperCase(),
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${localStorage.getItem("token") || ""}`
                            }
                        }
                    );
                    if (res.ok) {
                        let arr: any = [];
                        try {
                            arr = await res.json();
                        } catch { arr = []; }
                        if (Array.isArray(arr)) {
                            arr
                                .sort((a: any, b: any) => new Date(b.created_at || b.date).getTime() - new Date(a.created_at || a.date).getTime())
                                .slice(0, 10)
                                .forEach((obj: any) => {
                                    const notif = ep.parse(obj);
                                    allNotifs.push(notif);
                                });
                        }
                    }
                } catch {}
            }

            // Remove duplicates and keep only newest one per (id)
            let unique = new Map<string, NotificationType>();
            for (let notif of allNotifs) {
                if (!unique.has(notif.id)) unique.set(notif.id, notif);
            }
            let uniqueArr = Array.from(unique.values());
            uniqueArr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

            // Mark as read from localStorage
            const seenIds = getSeenIds();
            for (let n of uniqueArr) {
                if (seenIds.includes(n.id)) n.read = true;
            }

            // Only show last 20 notifications
            uniqueArr = uniqueArr.slice(0, 20);

            if (isMounted) {
                setNotifications((curr) => {
                    const ids1 = curr.map(n => n.id + (n.read ? "y" : "n")).join(".");
                    const ids2 = uniqueArr.map(n => n.id + (n.read ? "y" : "n")).join(".");
                    if (ids1 === ids2) return curr;
                    return uniqueArr;
                });
            }
        };

        fetchAll();
        const poll = setInterval(fetchAll, 12000);
        return () => {
            isMounted = false;
            clearInterval(poll);
        }
    }, []);

    // Close notifications dropdown on outside click
    useEffect(() => {
        if (!showNotifDropdown) return;
        function handleClick(e: any) {
            if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target)) {
                setShowNotifDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [showNotifDropdown]);

    // Mark notification as read & persist to localStorage
    const markNotificationRead = (notifId: string) => {
        setNotifications(nots => nots.map(n => n.id === notifId ? { ...n, read: true } : n));
        try {
            const LOCAL_STORAGE_KEY = 'seen_notif_ids';
            const seenNow = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "[]");
            if (!seenNow.includes(notifId)) {
                seenNow.push(notifId);
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(seenNow));
            }
        } catch { }
    };

    const userName = user?.name ? user.name : "User";
    const userProfileImg =
        user?.profile_img
            ? user.profile_img
            : "https://ui-avatars.com/api/?name=" + encodeURIComponent(userName);

    const unreadCount = notifications.filter(n => !n.read).length;
    const sortedNotifications = [...notifications].sort(
        (a, b) => (new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    );

    // Edit Profile UI handlers
    const openEditProfile = () => {
        if (user) {
            setEditUser({ ...user });
            setEditError(null);
            setShowEditProfile(true);
        }
    };

    const closeEditProfile = () => {
        setShowEditProfile(false);
        setEditError(null);
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditUser(u => u ? { ...u, [e.target.name]: e.target.value } : u);
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editUser) return;
        setIsSubmitting(true);
        setEditError(null);
        try {
            // Save profile for logged-in user: use /user/:id with PUT (original endpoint)
            const id = getUserIdFromToken();
            if (!id) throw new Error("No user id found.");
            const res = await fetch(`${apiurl}/user/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token") || ""}`
                },
                body: JSON.stringify(editUser)
            });
            if (!res.ok) {
                let msg = 'Failed to update profile.';
                try {
                    const d = await res.json();
                    if (typeof d === 'string') msg = d;
                    if (typeof d === 'object' && d.message) msg = d.message;
                } catch {}
                throw new Error(msg);
            }
            const data = await res.json();
            setUser(data);
            setShowEditProfile(false);
        } catch (err: any) {
            setEditError(err?.message || 'Failed to update profile');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <header className="flex flex-wrap justify-between items-center p-4 gap-y-4 border-[2px] border-[#ECEFF9]">
                <div className="w-full md:w-[48%] lg:w-[16%] flex-shrink-0">
                    <img
                        className="w-[81px] h-[26px] object-cover"
                        src={logo}
                        alt="Logo"
                    />
                </div>

                <div className="w-full md:w-[48%] lg:w-[22%] flex-shrink-0 hidden">
                    <h1 className="text-[20px] sm:text-[22px] font-semibold">
                        Welcome Back, {userName}!
                    </h1>
                </div>

                <div className="hidden w-full md:w-[48%] lg:w-[27%] bg-[#F4F6F8] h-[38px] flex items-center rounded-[6px] px-4">
                    <input
                        type="text"
                        placeholder="Search..."
                        className="w-full bg-transparent h-[32px] focus:outline-none"
                        value={searchValue}
                        onChange={e => onSearchChange(e.target.value)}
                    />
                    <CiSearch className="text-[#8488B7] text-[24px] cursor-pointer" />
                </div>

                <div className="w-full md:w-[48%] lg:w-[24%] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <img
                            className="w-[40px] h-[40px] object-cover rounded-full"
                            src={userProfileImg}
                            alt="Profile"
                        />
                        <div className="flex flex-col">
                            <span className="hidden sm:inline text-sm sm:text-base font-poppins">
                                {userName}
                            </span>
                            <button
                                className="text-xs text-blue-600 mt-0.5 underline text-left"
                                onClick={openEditProfile}
                                style={{ padding: 0, background: "none", border: "none", cursor: "pointer", alignSelf: 'flex-start' }}
                            >
                                Edit Profile
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <button
                            type="button"
                            aria-label={`Show notifications (${unreadCount})`}
                            onClick={() => setShowNotifDropdown(s => !s)}
                            className="relative p-1"
                        >
                            <CiBellOn className="text-[24px] text-[#3F4765]" />
                            {unreadCount > 0 &&
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 rounded-full">
                                    {unreadCount > 9 ? "9+" : unreadCount}
                                </span>
                            }
                        </button>
                        {showNotifDropdown && (
                            <div
                                ref={notifDropdownRef}
                                className="absolute right-0 z-20 mt-2 w-[340px] max-w-[95vw] bg-white border border-gray-200 rounded-md shadow-lg overflow-auto max-h-[460px]"
                            >
                                <div className="font-semibold text-base px-4 py-2 border-b bg-[#F9F5FF] text-[#6742c7]">Notifications</div>
                                {sortedNotifications.length === 0 ? (
                                    <div className="text-center text-gray-400 py-6 text-sm">No notifications yet.</div>
                                ) : (
                                    <ul>
                                        {sortedNotifications.map(n => (
                                            <li
                                                key={n.id}
                                                className={`px-4 py-3 border-b last:border-b-0 cursor-pointer flex gap-2 ${n.read ? "" : "bg-[#F5FAFC]"}`}
                                                onClick={() => markNotificationRead(n.id)}
                                            >
                                                <div className="flex-1">
                                                    <div className={`font-semibold ${n.read ? "text-gray-700" : "text-[#244B74]"}`}>
                                                        {n.title}
                                                    </div>
                                                    {n.description &&
                                                        <div className="text-xs text-gray-500 mt-0.5">{n.description}</div>
                                                    }
                                                    <div className="text-[11px] text-gray-400 mt-1">
                                                        {new Date(n.created_at).toLocaleString('en-IN', {
                                                            year: "numeric",
                                                            month: "short",
                                                            day: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit"
                                                        })}
                                                    </div>
                                                </div>
                                                {!n.read && <span className="h-3 w-3 rounded-full bg-blue-500 self-start mt-1"></span>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="p-2 text-center">
                                    <button
                                        className="text-[#6742c7] text-xs underline font-medium"
                                        onClick={() => setShowNotifDropdown(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* --- Edit Profile Modal (Basic) --- */}
            {showEditProfile && editUser &&
                <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-40" style={{ animation: 'fadeIn .2s' }}>
                    <div className="bg-white rounded-lg p-6 max-w-md w-full border shadow-md relative">
                        <button
                            onClick={closeEditProfile}
                            className="absolute right-3 top-2 text-gray-400 hover:text-gray-700 text-lg"
                            aria-label="Close"
                            style={{ background: 'none', border: 'none' }}
                        >&times;</button>
                        <h2 className="font-bold text-xl mb-4 text-[#6742c7]">Edit Profile</h2>
                        <form onSubmit={handleProfileSubmit} className="flex flex-col gap-3">
                            <label className="text-sm font-medium flex flex-col gap-1">
                                Name
                                <input
                                    name="name"
                                    type="text"
                                    value={editUser.name || ''}
                                    onChange={handleEditChange}
                                    className="border rounded px-2 py-1"
                                    required
                                />
                            </label>
                            <label className="text-sm font-medium flex flex-col gap-1">
                                Email
                                <input
                                    name="email"
                                    type="email"
                                    value={editUser.email || ''}
                                    onChange={handleEditChange}
                                    className="border rounded px-2 py-1"
                                />
                            </label>
                            <label className="text-sm font-medium flex flex-col gap-1">
                                Mobile
                                <input
                                    name="mobile"
                                    type="text"
                                    value={editUser.mobile || ''}
                                    onChange={handleEditChange}
                                    className="border rounded px-2 py-1"
                                />
                            </label>
                            <label className="text-sm font-medium flex flex-col gap-1">
                                Profile Image URL<br />
                                <input
                                    name="profile_img"
                                    type="url"
                                    value={editUser.profile_img || ''}
                                    onChange={handleEditChange}
                                    className="border rounded px-2 py-1"
                                    placeholder="https://example.com/img.png"
                                />
                            </label>
                            {editError && <div className="text-red-600 text-xs">{editError}</div>}
                            <div className="flex justify-end gap-2 mt-2">
                                <button type="button" onClick={closeEditProfile} className="px-3 py-1 border rounded text-gray-600 bg-gray-100 hover:bg-gray-200">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-[#6742c7] text-white px-3 py-1 rounded hover:bg-[#5432a7] disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            }
        </>
    )
}

export default Header
